/**
 * Telegram Mini App chrome helpers (Bot API 8.0+ fullscreen + safe areas).
 * iPhone notch / Dynamic Island / home indicator need larger floors than Android.
 */

const HEADER_BG = '#161311';
const FS_PREF_KEY = 'vq_puzzle_fs';
/** Auto-request FS only when estimated stage budget grows by at least this many px. */
const FS_AUTO_GAIN_PX = 12;

export function getTelegramWebApp() {
    return window.Telegram?.WebApp || null;
}

export function isTelegramFullscreen() {
    const tg = getTelegramWebApp();
    return Boolean(tg?.isFullscreen);
}

export function isAppleTouchDevice() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS desktop UA
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function insetPx(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

function readCssVarPx(name) {
    try {
        const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
        if (!raw) return 0;
        const n = parseFloat(raw);
        return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
    } catch {
        return 0;
    }
}

/**
 * Combined device + Telegram UI insets (max of each side).
 */
export function getTelegramInsets() {
    const tg = getTelegramWebApp();
    const safe = tg?.safeAreaInset || {};
    const content = tg?.contentSafeAreaInset || {};
    const top = Math.max(
        insetPx(safe.top),
        insetPx(content.top),
        readCssVarPx('--tg-safe-area-inset-top'),
        readCssVarPx('--tg-content-safe-area-inset-top')
    );
    const bottom = Math.max(
        insetPx(safe.bottom),
        insetPx(content.bottom),
        readCssVarPx('--tg-safe-area-inset-bottom'),
        readCssVarPx('--tg-content-safe-area-inset-bottom')
    );
    const left = Math.max(
        insetPx(safe.left),
        insetPx(content.left),
        readCssVarPx('--tg-safe-area-inset-left'),
        readCssVarPx('--tg-content-safe-area-inset-left')
    );
    const right = Math.max(
        insetPx(safe.right),
        insetPx(content.right),
        readCssVarPx('--tg-safe-area-inset-right'),
        readCssVarPx('--tg-content-safe-area-inset-right')
    );
    return { top, bottom, left, right };
}

/** Floor insets when Telegram under-reports (only used when reported side is 0). */
function floorInsetsFor({ fullscreen, landscape, ios }) {
    let top = 0;
    let right = 0;
    let bottom = 0;
    let left = 0;
    if (ios) {
        top = fullscreen ? 112 : 56;
        bottom = 34;
        if (landscape) {
            left = 48;
            right = 56;
            top = Math.max(top, fullscreen ? 64 : 28);
        } else if (fullscreen) {
            right = 20;
        }
    } else if (fullscreen) {
        top = landscape ? 56 : 64;
        right = landscape ? 96 : 80;
        bottom = 16;
        left = landscape ? 16 : 8;
    } else {
        top = 28;
        right = 12;
    }
    return { top, right, bottom, left };
}

function mergeInsetSide(reported, floor) {
    return reported > 0 ? reported : floor;
}

/**
 * Push inset CSS variables so HUD / board clear:
 * - iOS status bar + notch / Dynamic Island
 * - Telegram fullscreen Close + chevron/menu
 * - iPhone landscape side safe areas (notch on the side)
 * - home indicator
 *
 * Trust positive Telegram/CSS insets; apply floors only when a side reports 0.
 */
export function applyTelegramSafeAreaVars(target = document.documentElement) {
    if (!target?.style) return getTelegramInsets();
    const insets = getTelegramInsets();
    const fs = isTelegramFullscreen();
    const ios = isAppleTouchDevice();
    const landscape =
        (typeof window !== 'undefined' &&
            (window.matchMedia('(orientation: landscape)').matches ||
                window.innerWidth > window.innerHeight)) ||
        false;

    const floors = floorInsetsFor({ fullscreen: fs, landscape, ios });
    const top = mergeInsetSide(insets.top, floors.top);
    const right = mergeInsetSide(insets.right, floors.right);
    const bottom = mergeInsetSide(insets.bottom, floors.bottom);
    const left = mergeInsetSide(insets.left, floors.left);

    target.style.setProperty('--vq-inset-top', `${top}px`);
    target.style.setProperty('--vq-inset-right', `${right}px`);
    target.style.setProperty('--vq-inset-bottom', `${bottom}px`);
    target.style.setProperty('--vq-inset-left', `${left}px`);
    target.style.setProperty('--vq-hud-top', `${top}px`);
    target.style.setProperty('--vq-hud-right', `${right}px`);
    target.style.setProperty('--vq-hud-left', `${left}px`);
    target.style.setProperty('--vq-hud-bottom', `${bottom}px`);
    target.dataset.vqIos = ios ? '1' : '0';
    target.dataset.vqFs = fs ? '1' : '0';
    return { top, right, bottom, left, ios, fs };
}

/** True for phone / narrow Telegram panels — use viewport-maximize board sizing. */
export function isPhonePuzzleViewport() {
    if (typeof window === 'undefined') return true;
    const w = window.visualViewport?.width ?? window.innerWidth;
    const h = window.visualViewport?.height ?? window.innerHeight;
    const shortEdge = Math.min(w, h);
    if (shortEdge < 560) return true;
    // Tall phone in landscape can have shortEdge ≥ 560 on some devices — keep phone if narrow long edge
    const longEdge = Math.max(w, h);
    if (longEdge < 820 && window.matchMedia('(pointer: coarse)').matches) return true;
    return false;
}

export function getPuzzleFullscreenPref() {
    try {
        return sessionStorage.getItem(FS_PREF_KEY) === '1';
    } catch {
        return false;
    }
}

export function setPuzzleFullscreenPref(on) {
    try {
        sessionStorage.setItem(FS_PREF_KEY, on ? '1' : '0');
    } catch {
        /* ignore */
    }
}

export function canTogglePuzzleFullscreen() {
    if (!isPhonePuzzleViewport()) return false;
    const tg = getTelegramWebApp();
    return typeof tg?.requestFullscreen === 'function';
}

/**
 * Rough stage budget (before clue gutter) for comparing FS vs non-FS chrome.
 * Dense overlay assumes floating Check + overlay header (tools rail/row only).
 */
export function estimatePuzzleStageBudget({
    fullscreen = isTelegramFullscreen(),
    landscape = null,
    denseChrome = true,
} = {}) {
    if (typeof window === 'undefined') return 0;
    const vvW = window.visualViewport?.width ?? window.innerWidth;
    const vvH = window.visualViewport?.height ?? window.innerHeight;
    const isLandscape =
        landscape != null
            ? landscape
            : window.matchMedia('(orientation: landscape)').matches || vvW > vvH;
    const ios = isAppleTouchDevice();
    const insets = getTelegramInsets();
    const floors = floorInsetsFor({ fullscreen, landscape: isLandscape, ios });
    const top = mergeInsetSide(insets.top, floors.top);
    const right = mergeInsetSide(insets.right, floors.right);
    const bottom = mergeInsetSide(insets.bottom, floors.bottom);
    const left = mergeInsetSide(insets.left, floors.left);
    const edge = 4;
    let chromeX = edge + left + right;
    let chromeY = edge + top + bottom;
    if (denseChrome) {
        if (isLandscape) chromeX += 48;
        else chromeY += 40;
    } else {
        chromeY += 36 + 48 + 52; // header + Check + tools
    }
    return Math.floor(Math.min(Math.max(0, vvW - chromeX), Math.max(0, vvH - chromeY)));
}

/** True when Telegram fullscreen is expected to enlarge the puzzle square. */
export function shouldAutoRequestPuzzleFullscreen({ denseChrome = true } = {}) {
    if (!isPhonePuzzleViewport()) return false;
    if (!canTogglePuzzleFullscreen()) return false;
    if (getPuzzleFullscreenPref()) return true;
    if (isTelegramFullscreen()) return false;
    const withFs = estimatePuzzleStageBudget({ fullscreen: true, denseChrome });
    const without = estimatePuzzleStageBudget({ fullscreen: false, denseChrome });
    return withFs >= without + FS_AUTO_GAIN_PX;
}

/** expand() + colors + safe-area — no requestFullscreen. */
export function expandPuzzleViewport() {
    if (!isPhonePuzzleViewport()) return false;
    const tg = getTelegramWebApp();
    if (!tg) {
        applyTelegramSafeAreaVars();
        return false;
    }
    try {
        if (typeof tg.setHeaderColor === 'function') tg.setHeaderColor(HEADER_BG);
        if (typeof tg.setBackgroundColor === 'function') tg.setBackgroundColor(HEADER_BG);
        if (typeof tg.expand === 'function') tg.expand();
        applyTelegramSafeAreaVars();
        return true;
    } catch (e) {
        console.warn('[Telegram] expand failed', e);
        applyTelegramSafeAreaVars();
        return false;
    }
}

/**
 * Request Telegram fullscreen on phones.
 * @param {{ force?: boolean, denseChrome?: boolean }} opts
 *   force — user tapped Expand (ignore auto gain heuristic)
 */
export function requestPuzzleFullscreen({ force = false, denseChrome = true } = {}) {
    if (!isPhonePuzzleViewport()) return false;
    expandPuzzleViewport();
    const tg = getTelegramWebApp();
    if (!tg || typeof tg.requestFullscreen !== 'function') return false;
    if (tg.isFullscreen) return true;
    if (!force && !shouldAutoRequestPuzzleFullscreen({ denseChrome })) return false;
    try {
        tg.requestFullscreen();
        window.setTimeout(() => applyTelegramSafeAreaVars(), 50);
        window.setTimeout(() => applyTelegramSafeAreaVars(), 250);
        window.setTimeout(() => applyTelegramSafeAreaVars(), 600);
        return true;
    } catch (e) {
        console.warn('[Telegram] requestFullscreen failed', e);
    }
    return false;
}

export function exitPuzzleFullscreen() {
    const tg = getTelegramWebApp();
    if (!tg || typeof tg.exitFullscreen !== 'function') return;
    try {
        if (tg.isFullscreen) tg.exitFullscreen();
    } catch (e) {
        console.warn('[Telegram] exitFullscreen failed', e);
    }
}

/**
 * After FS settles: if board budget did not grow, exit and remember not to auto-FS.
 * Returns true if FS was kept.
 */
export function verifyPuzzleFullscreenHelped({ beforeBudget, denseChrome = true } = {}) {
    if (!isTelegramFullscreen()) return false;
    const after = estimatePuzzleStageBudget({ fullscreen: true, denseChrome });
    const baseline =
        typeof beforeBudget === 'number'
            ? beforeBudget
            : estimatePuzzleStageBudget({ fullscreen: false, denseChrome });
    if (after + 4 < baseline) {
        setPuzzleFullscreenPref(false);
        exitPuzzleFullscreen();
        window.setTimeout(() => applyTelegramSafeAreaVars(), 80);
        return false;
    }
    return true;
}

/** Subscribe to viewport/fullscreen changes; returns unsubscribe. */
export function onTelegramViewportChange(handler) {
    const tg = getTelegramWebApp();
    const wrapped = () => {
        applyTelegramSafeAreaVars();
        handler();
    };
    if (!tg?.onEvent) {
        window.addEventListener('resize', wrapped);
        window.addEventListener('orientationchange', wrapped);
        return () => {
            window.removeEventListener('resize', wrapped);
            window.removeEventListener('orientationchange', wrapped);
        };
    }
    const events = [
        'fullscreenChanged',
        'fullscreenFailed',
        'viewportChanged',
        'safeAreaChanged',
        'contentSafeAreaChanged',
    ];
    events.forEach((ev) => tg.onEvent(ev, wrapped));
    window.addEventListener('orientationchange', wrapped);
    applyTelegramSafeAreaVars();
    return () => {
        events.forEach((ev) => tg.offEvent?.(ev, wrapped));
        window.removeEventListener('orientationchange', wrapped);
    };
}
